import { cn } from '@/lib/utils/cn';

interface Option {
  id: number;
  name: string;
}

interface FilterSelectProps<T extends Option> {
  label: string;
  placeholder: string;
  value: T | null;
  options: T[];
  onChange: (option: T | null) => void;
  onClear: () => void;
  disabled?: boolean;
}

/**
 * One cascading dropdown. Its option list is already constrained to the valid
 * combinations for the upstream selection, so picking any value keeps the form
 * self-consistent. A clear (×) button resets this field and everything below it.
 */
export function FilterSelect<T extends Option>({
  label,
  placeholder,
  value,
  options,
  onChange,
  onClear,
  disabled = false,
}: FilterSelectProps<T>) {
  const isDisabled = disabled || options.length === 0;

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-semibold text-ink-soft">{label}</label>
      <div className="relative">
        <select
          value={value?.id ?? ''}
          disabled={isDisabled}
          onChange={(e) => {
            const id = e.target.value;
            onChange(id === '' ? null : (options.find((o) => String(o.id) === id) ?? null));
          }}
          className={cn(
            'h-11 w-full appearance-none rounded-input border border-muted-300 bg-white pl-3 pr-16 text-sm text-ink',
            'focus-visible:border-primary focus-visible:outline-none',
            'disabled:cursor-not-allowed disabled:bg-muted-100 disabled:text-ink-mute',
          )}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>

        {/* Clear + chevron */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
          {value && !disabled && (
            <button
              type="button"
              onClick={onClear}
              className="pointer-events-auto rounded-full p-1 text-ink-mute hover:bg-muted-100 hover:text-ink"
              aria-label={`Șterge ${label.toLowerCase()}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
          )}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ink-mute">
            <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
    </div>
  );
}
